import { DownloadOutlined } from '@ant-design/icons';
import { MuiThemeProvider, Switch } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import { Button, Empty, Space, Spin, Tooltip, Typography } from 'antd';
import { MagnifyingGlass } from 'phosphor-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import InputText from '~/components/InputText';
import { useSpecialFeatureUnlock } from '~/contexts/SpecialFeatureUnlockContext';
import api from '~/services/api';
import history from '~/services/history';

import { Datatable } from './Components/Datatable';
import InventorySummaryByLocation from './InventorySummaryByLocation';
import { Container } from './styles';

export default function InventoryComplete({ location }) {
  const { isSpecialFeatureEnabled, isKonamiUnlocked } = useSpecialFeatureUnlock();
  const shouldEnableSpecialFeature = !isSpecialFeatureEnabled || isKonamiUnlocked;
  const backendSpecialFeatureEnabled = isSpecialFeatureEnabled && !isKonamiUnlocked;
  const [searchTxt, setSearchTxt] = useState('');
  const [zeroQty, setZeroQty] = useState(false);
  const [inventoryLLC, setInventoryLLC] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedLocations, setCheckedLocations] = useState(null);

  const searchInputRef = useRef(searchTxt);

  const { Paragraph } = Typography;

  const handleLocationFilterChange = useCallback(
    async (param) => {
      const params = new URLSearchParams();
      params.append('searchQuery', encodeURIComponent(searchTxt));
      params.append('zeroQty', zeroQty);
      params.append('inventoryLLC', inventoryLLC);
      params.append('specialFeature', backendSpecialFeatureEnabled ? 'true' : 'false');
      params.append('locationFilters', param);

      const response = await api.get(`inventory?${params}`);

      if (!response.data.ok) {
        toast.warn('No items found!');
        return;
      }
      setInventory(response.data);
    },
    [searchTxt, zeroQty, inventoryLLC, backendSpecialFeatureEnabled]
  );

  const handleSearch = useCallback(
    (searchQueryParam = '') => {
      // Build the new URL params from the current UI state and requested query
      const query = String(searchQueryParam || '').trim();

      // update local search text state
      setSearchTxt(query);

      const params = new URLSearchParams();
      params.append('searchQuery', encodeURIComponent(query));
      params.append('zeroQty', zeroQty ? 'true' : 'false');
      params.append('inventoryLLC', inventoryLLC ? 'true' : 'false');

      if (checkedLocations) {
        params.append('locationFilters', checkedLocations);
      }

      // Only update the URL. The existing useEffect that watches `location`
      // will perform the single API request and update inventory.
      history.replace(`inventoryComplete?${params}`);
    },
    [checkedLocations, zeroQty, inventoryLLC]
  );

  // useEffect(() => {
  //   async function loadData() {
  //     const { data: assistantUsersData } = await api.get('params?service=Assistant&key_param=usersAllowed');
  //     setUsersAllowedAssistant(assistantUsersData.split(';'));
  //     }
  //   loadData();
  // }, []);

  useEffect(() => {
    async function loadItems() {
      try {
        const { search } = location;
        const paramsUrl = new URLSearchParams(search);
        const searchQuery = paramsUrl.get('searchQuery');
        const showZeroQuery = paramsUrl.get('zeroQty');
        const inventoryLLC = paramsUrl.get('inventoryLLC');

        if (searchQuery === null) return;

        setZeroQty(() => showZeroQuery === 'true');
        setInventoryLLC(() => inventoryLLC === 'true');
        setSearchTxt(() => decodeURIComponent(searchQuery).trim());

        setLoading(true);

        const params = new URLSearchParams();
        params.append('searchQuery', searchQuery.trim());
        params.append('zeroQty', showZeroQuery);
        params.append('inventoryLLC', inventoryLLC);
        params.append('specialFeature', backendSpecialFeatureEnabled ? 'true' : 'false');

        if (checkedLocations) {
          params.append('locationFilters', checkedLocations);
        }

        const response = await api.get(`inventoryComplete?${params}`);
        // New API returns an object with inventoryCount array. Check both shapes.
        if ((!response.data || (Array.isArray(response.data.inventoryCount) && response.data.inventoryCount.length === 0)) && searchQuery !== null) {
          toast.warn('No items found!');
        }
        setInventory(response.data);

        setLoading(false);
        if (searchQuery) {
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.value = decodeURIComponent(searchQuery).trim();
              searchInputRef.current.focus();
            }
          }, 0);
        }
      } catch (error) {
        toast.error(error.message);
      }
    }

    loadItems();
  }, [checkedLocations, location, backendSpecialFeatureEnabled]);

  function columnLink(value, tableMeta) {
    // Find the item data
    const item = tableMeta.tableData.find((el) => el[3] === value || el.part_number === value);
    const itemId = item[0] || item.id;

    // Check if this item has a NOTL item record (regardless of stock level)
    let hasNOTLItem = false;
    let hasInventoryInLLC = false;

    if (inventory && inventory.inventoryCount) {
      const inventoryItem = inventory.inventoryCount.find((inv) => inv.part_number === value);
      if (inventoryItem && inventoryItem.quantities) {
        // Check if there's a NOTL item record (not just if it has stock)
        hasNOTLItem = inventoryItem.quantities.local !== undefined || inventoryItem.quantities.allocated !== undefined;
        const usTotal = inventoryItem.quantities.us?.total || 0;
        hasInventoryInLLC = usTotal > 0;
      }
    }

    // If no NOTL item record exists (only LLC), return plain text
    if (!hasNOTLItem) {
      const content = (
        <Paragraph style={{ color: '#000', margin: 0 }} copyable>
          {value}
        </Paragraph>
      );

      if (hasInventoryInLLC) {
        return (
          <Tooltip title="Stock only available in LLC" overlayStyle={{ maxWidth: '300px' }}>
            {content}
          </Tooltip>
        );
      }

      return content;
    }

    // If there's a NOTL item record, show the link (even if stock is 0)
    return (
      <Link
        to={() => {
          if (searchTxt === null || searchTxt === undefined) {
            return `inventoryEdit/${itemId}`;
          }
          // Add inventoryLLC=true if the item has inventory in LLC location
          const shouldShowLLC = inventoryLLC && hasInventoryInLLC;
          return `inventoryEdit/${itemId}?searchQuery=${searchTxt}&showZero=${zeroQty}&inventoryLLC=${shouldShowLLC}`;
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Paragraph style={{ color: '#1890ff' }} copyable>
          {value}
        </Paragraph>
      </Link>
    );
  }

  // Helper to render quantity cells: hide zero values, show "local / us" when provided as a string
  function renderQty(value) {
    if (value === null || value === undefined || value === '') return '';
    // numeric zero
    if (typeof value === 'number') return value === 0 ? '' : value;
    // strings like "0" or "0 / 0" should be hidden
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '0' || trimmed === '0 / 0' || trimmed === '0/0') return '';
      return trimmed;
    }
    return value;
  }

  const columns = [
    {
      name: 'id',
      label: 'id',
      options: {
        filter: false,
        sort: false,
        display: false,
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        // filterList: ['Kioxia'],
        sort: true,
      },
    },
    {
      name: 'category',
      label: 'Category',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'part_number',
      label: 'Part Number',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => columnLink(value, tableMeta),
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Paragraph
            style={{
              maxWidth: '450px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: 0,
            }}
            copyable
            ellipsis={{
              rows: 3,
              expandable: false,
              tooltip: {
                title: value,
                overlayStyle: { maxWidth: '600px' },
              },
            }}
          >
            {value}
          </Paragraph>
        ),
      },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        display: true,
      },
    },
    {
      name: 'cpu',
      label: 'CPU',
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: 'ram',
      label: 'RAM',
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: 'screen_type',
      label: 'Screen Type',
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: 'New',
      label: 'New',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'OB',
      label: 'OB',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'U',
      label: 'U',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'A',
      label: 'A',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },

    {
      name: 'B',
      label: 'B',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'C',
      label: 'C',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'D',
      label: 'D',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'FA',
      label: 'FA',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'TBI',
      label: 'TBI',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => renderQty(value),
      },
    },
    {
      name: 'total',
      label: 'Total',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'details',
      label: 'Details',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => (
          <Link
            to={() =>
              `/inventoryDetails?partNumber=${tableMeta.rowData[3]}&searchQuery=${encodeURIComponent(
                searchTxt.trim()
              )}&showZero=${zeroQty}&inventoryLLC=${inventoryLLC}`
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <InfoIcon />
          </Link>
        ),
      },
    },
  ];

  // Transform incoming inventory into the shape previously expected by the table.
  // When inventoryLLC is true, show "local / us" in each quantity cell so users can distinguish.
  const transformedInventory = useMemo(() => {
    if (!inventory || !inventory.inventoryCount) return [];

    const statuses = ['New', 'OB', 'U', 'A', 'B', 'C', 'D', 'FA', 'TBI', 'null'];

    return inventory.inventoryCount.map((item) => {
      const out = { ...item };

      // If new API shape with quantities object
      if (item.quantities && typeof item.quantities === 'object') {
        const local = item.quantities.local || {};
        const us = item.quantities.us || {};
        const allocated = item.quantities.allocated || {};

        // Store sort values (NOTL + Allocated, ignoring LLC) as hidden properties
        out._sortValues = {};

        statuses.forEach((s) => {
          const localVal = local[s] !== undefined ? local[s] : 0;
          const usVal = us[s] !== undefined ? us[s] : 0;
          const allocatedVal = allocated[s] !== undefined ? allocated[s] : 0;

          // Store the combined value for sorting (NOTL + Allocated + LLC)
          out._sortValues[s] = localVal + allocatedVal + usVal;

          // Show multi-line format if allocated > 0 OR (inventoryLLC AND us > 0)
          const shouldShowMultiLine = allocatedVal > 0 || (inventoryLLC && usVal > 0);

          if (shouldShowMultiLine) {
            out[s] = (
              <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
                <div style={{ whiteSpace: 'nowrap' }}>
                  <strong>NOTL:</strong> {localVal}
                </div>
                {inventoryLLC && usVal > 0 && (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <strong>LLC:</strong> {usVal}
                  </div>
                )}
                {allocatedVal > 0 && (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <strong>Allocated:</strong> {allocatedVal}
                  </div>
                )}
              </div>
            );
          } else {
            out[s] = localVal;
          }
        });

        const localTotal = local.total !== undefined && local.total !== null ? local.total : 0;
        const usTotal = us.total !== undefined && us.total !== null ? us.total : 0;
        const allocatedTotal = allocated.total !== undefined && allocated.total !== null ? allocated.total : 0;

        // Store the combined total for sorting (NOTL + Allocated + LLC)
        out._sortValues.total = localTotal + allocatedTotal + usTotal;

        // Show multi-line format if allocated > 0 OR (inventoryLLC AND us > 0)
        const shouldShowMultiLine = allocatedTotal > 0 || (inventoryLLC && usTotal > 0);

        if (shouldShowMultiLine) {
          out.total = (
            <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
              <div style={{ whiteSpace: 'nowrap' }}>
                <strong>NOTL:</strong> {localTotal}
              </div>
              {inventoryLLC && usTotal > 0 && (
                <div style={{ whiteSpace: 'nowrap' }}>
                  <strong>LLC:</strong> {usTotal}
                </div>
              )}
              {allocatedTotal > 0 && (
                <div style={{ whiteSpace: 'nowrap' }}>
                  <strong>Allocated:</strong> {allocatedTotal}
                </div>
              )}
            </div>
          );
        } else {
          out.total = localTotal;
        }
      } else {
        // Old API: quantities were top-level. Ensure keys exist for statuses and total.
        statuses.forEach((s) => {
          if (out[s] === undefined) out[s] = 0;
        });
        if (out.total === undefined) out.total = out.total || 0;
      }

      return out;
    });
  }, [inventory, inventoryLLC]);

  const options = {
    fixedHeaderOptions: { xAxis: true, yAxis: true },
    filterType: 'multiselect',
    expandableRowsOnClick: false,
    pagination: true,
    download: false,
    print: false,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 150],
    search: false,
    responsive: 'scrollMaxHeight',
    checkboxSelection: false,
    selectableRows: false,
    selectableRowsOnClick: false,
    tableBodyHeight: '799px',
    tableBodyMaxHeight: 'calc(100vh - 348px) !important',

    /**
     * Callback function that triggers when filters have changed. function(changedColumn: string, filterList: array, type: enum('checkbox', 'dropdown', 'multiselect', 'textField', 'custom', 'chip', 'reset'), changedColumnIndex, displayData) => void
     */
    // onFilterChange: (changedColumn, filterList, type) => {
    //   console.log('changedColumn, filterList, type ', changedColumn, filterList, type);
    //   // if (type === 'reset') {
    //   //   setCheckedLocations(null);
    //   //   return;
    //   // }
    //   // const checked = filterList.map((el) => el[0]);
    //   // setCheckedLocations(checked);
    // },

    customToolbarSelect: () => {},
    customSort: (data, colIndex, order) =>
      data.sort((a, b) => {
        let aValue = a.data[colIndex];
        let bValue = b.data[colIndex];

        // Get the column name to check if we have a stored sort value
        const columnName = columns[colIndex]?.name;

        // Quantity columns that need numeric sorting
        const quantityColumns = ['New', 'OB', 'U', 'A', 'B', 'C', 'D', 'FA', 'TBI', 'total'];

        // If it's a quantity column, use numeric sorting
        if (quantityColumns.includes(columnName)) {
          // Helper function to extract numeric value for sorting
          const extractSortValue = (value, rowData) => {
            if (value === null || value === undefined || value === '') return 0;

            // If it's a React element (multi-line format), use the stored sort value
            if (typeof value === 'object' && value.props) {
              // Try to find the item in transformedInventory by matching the part_number (index 3)
              const partNumber = rowData[3]; // part_number is column index 3
              const item = transformedInventory.find((inv) => inv.part_number === partNumber);

              if (item && item._sortValues && item._sortValues[columnName] !== undefined) {
                return item._sortValues[columnName];
              }
              return 0;
            }

            // If it's a number, return it directly
            if (typeof value === 'number') return value;

            // If it's a string, try to parse as number
            if (typeof value === 'string') {
              const parsed = parseFloat(value);
              return Number.isNaN(parsed) ? 0 : parsed;
            }

            return 0;
          };

          aValue = extractSortValue(aValue, a.data);
          bValue = extractSortValue(bValue, b.data);

          if (order === 'asc') return aValue - bValue;
          return bValue - aValue;
        }

        // For text columns, use string comparison
        const aStr = aValue === null || aValue === undefined ? '' : String(aValue).toLowerCase();
        const bStr = bValue === null || bValue === undefined ? '' : String(bValue).toLowerCase();

        if (order === 'asc') return aStr.localeCompare(bStr);
        return bStr.localeCompare(aStr);
      }),
  };

  // Custom download function that sums NOTL + Allocated (ignores LLC)
  const handleDownloadCSV = () => {
    // Build CSV header from visible columns
    const visibleColumns = columns.filter((col) => col.options?.display !== false);
    const headerRow = visibleColumns.map((col) => `"${col.label}"`).join(',');

    // Build CSV rows with summed values
    const csvRows = transformedInventory.map((sourceItem) => {
      const rowValues = visibleColumns.map((col) => {
        const columnName = col.name;

        // For quantity columns and total - sum NOTL + Allocated (ignore LLC)
        if (
          sourceItem.quantities &&
          (columnName === 'New' ||
            columnName === 'OB' ||
            columnName === 'U' ||
            columnName === 'A' ||
            columnName === 'B' ||
            columnName === 'C' ||
            columnName === 'D' ||
            columnName === 'FA' ||
            columnName === 'TBI' ||
            columnName === 'total')
        ) {
          const local = sourceItem.quantities.local || {};
          const allocated = sourceItem.quantities.allocated || {};

          const localVal = local[columnName] !== undefined ? local[columnName] : 0;
          const allocatedVal = allocated[columnName] !== undefined ? allocated[columnName] : 0;

          // Return sum of NOTL + Allocated
          return localVal + allocatedVal;
        }

        // For details column (icon), skip it
        if (columnName === 'details') {
          return '';
        }

        // For other primitive columns
        if (
          columnName === 'id' ||
          columnName === 'part_number' ||
          columnName === 'brand' ||
          columnName === 'category' ||
          columnName === 'description' ||
          columnName === 'name' ||
          columnName === 'cpu' ||
          columnName === 'ram' ||
          columnName === 'screen_type'
        ) {
          const val = sourceItem[columnName];
          if (val === null || val === undefined) return '';
          if (typeof val === 'string') return `"${val.replace(/"/g, '""')}"`;
          if (typeof val === 'number') return val;
          return '';
        }

        return '';
      });

      return rowValues.join(',');
    });

    const csvContent = [headerRow, ...csvRows].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_complete_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getMuiTheme = () =>
    createTheme({
      overrides: {
        MUIDataTableBodyCell: {
          root: {
            margin: 0,
            paddingBottom: '2px',
          },
        },
        MUIDataTable: {
          responsiveScrollMaxHeight: {
            maxHeight: 'calc(100vh - 348px) !important',
          },
        },
      },
    });

  const theme = getMuiTheme();

  if (loading) {
    return (
      <Container>
        <Spin />
      </Container>
    );
  }

  return (
    <Container>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <InputText
            ref={searchInputRef}
            placeholder="Search"
            autoComplete="off"
            autoFocus
            onBlur={(e) => {
              e.target.value = e.target.value.toUpperCase().trim();
            }}
            IconAfterProp={<MagnifyingGlass onClick={() => handleSearch(searchInputRef.current.value)} style={{ cursor: 'pointer' }} size={20} />}
            onPressEnter={handleSearch}
          />
          {/** the label of this switch should never break into multiple lines */}
          <span style={{ whiteSpace: 'nowrap' }}>
            <Switch checked={zeroQty} onChange={(e) => setZeroQty(e.target.checked)} name="zeroQty" color="primary" />
            Show items with zero qty
          </span>

          {!process.env.REACT_APP_API_URL.includes('rbmsllcapi') && shouldEnableSpecialFeature && (
            <span style={{ whiteSpace: 'nowrap' }}>
              <Switch
                checked={inventoryLLC}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setInventoryLLC(newValue);
                  const params = new URLSearchParams();
                  params.append('searchQuery', encodeURIComponent(searchTxt));
                  params.append('zeroQty', zeroQty ? 'true' : 'false');
                  params.append('inventoryLLC', newValue ? 'true' : 'false');
                  if (checkedLocations) {
                    params.append('locationFilters', checkedLocations);
                  }
                  history.replace(`inventoryComplete?${params}`);
                }}
                name="inventoryLLC"
                color="primary"
              />
              Show inventory in RB LLC
            </span>
          )}
          {inventory.inventoryByLocation && Object.keys(inventory.inventoryByLocation).length > 0 && (
            <InventorySummaryByLocation
              data={inventory.inventoryByLocation}
              setCheckedLocations={setCheckedLocations}
              checkedLocations={checkedLocations}
              handleSearch={handleSearch}
              handleLocationFilterChange={handleLocationFilterChange}
            />
          )}
        </span>
        <span>
          <Space style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadCSV}
              disabled={!inventory.inventoryCount || inventory.inventoryCount.length === 0}
            >
              Download CSV
            </Button>
          </Space>
        </span>
      </Space>

      {inventory.inventoryCount && inventory.inventoryCount.length > 0 ? (
        <MuiThemeProvider theme={theme}>
          <Datatable title="Inventory" data={transformedInventory} columns={columns} options={options} />
        </MuiThemeProvider>
      ) : (
        <Empty
          description={<span>Use the search box above to list inventory</span>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '100vh',
          }}
        />
      )}
    </Container>
  );
}
